const LOG_PREFIX = "api_fail_logs";
const MAX_LOGS_PER_DAY = 200;

export function getEnvironment() {
    return process.env.VUE_APP_ENV || process.env.NODE_ENV || "production";
}

function getTodayKey() {
    return new Date().toISOString().slice(0, 10);
}

function buildStorageKey(env, dateKey) {
    return `${LOG_PREFIX}__${env}__${dateKey}`;
}

function _readKey(key) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function getBrowserInfo() {
    const ua = navigator.userAgent;
    let browser = "Unknown";

    if (ua.includes("Firefox/")) {
        browser = "Firefox " + ua.split("Firefox/")[1].split(" ")[0];
    } else if (ua.includes("Edg/")) {
        browser = "Edge " + ua.split("Edg/")[1].split(" ")[0];
    } else if (ua.includes("Chrome/")) {
        browser = "Chrome " + ua.split("Chrome/")[1].split(" ")[0];
    } else if (ua.includes("Safari/") && !ua.includes("Chrome")) {
        browser = "Safari " + ua.split("Version/")[1]?.split(" ")[0];
    }

    return browser;
}

function getDeviceInfo() {
    const ua = navigator.userAgent;
    const platform = navigator.platform || "Unknown";
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(ua);
    return {
        platform,
        type: isMobile ? "Mobile" : "Desktop",
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
    };
}

function getCurrentUser() {
    return (
        localStorage.getItem("user_name") ||
        localStorage.getItem("username") ||
        "Unknown"
    );
}

function getCurrentRoute() {
    const hash = window.location.hash || "";
    return hash.replace("#", "") || window.location.pathname;
}

export function addFailLog({
    apiUrl,
    method,
    params,
    statusCode,
    errorMessage,
}) {
    const env = getEnvironment();
    const dateKey = getTodayKey();
    const storageKey = buildStorageKey(env, dateKey);
    const logs = _readKey(storageKey);

    const log = {
        id: Date.now() + "_" + Math.random().toString(36).substring(2, 8),
        timestamp: new Date().toISOString(),
        user: getCurrentUser(),
        device: getDeviceInfo(),
        browser: getBrowserInfo(),
        route: getCurrentRoute(),
        apiUrl,
        method: (method || "get").toUpperCase(),
        params: sanitizeParams(params),
        statusCode: statusCode || null,
        errorMessage: errorMessage || "Unknown error",
    };

    logs.unshift(log);

    // Keep only the latest MAX_LOGS_PER_DAY entries per day
    if (logs.length > MAX_LOGS_PER_DAY) {
        logs.length = MAX_LOGS_PER_DAY;
    }

    try {
        localStorage.setItem(storageKey, JSON.stringify(logs));
    } catch (e) {
        // localStorage full — remove oldest half and retry
        logs.length = Math.floor(MAX_LOGS_PER_DAY / 2);
        localStorage.setItem(storageKey, JSON.stringify(logs));
    }

    return log;
}

export function getFailLogs(env, dateKey) {
    const e = env || getEnvironment();
    const d = dateKey || getTodayKey();
    return _readKey(buildStorageKey(e, d));
}

export function getAllLogMeta() {
    const result = [];
    const prefix = LOG_PREFIX + "__";
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        const rest = key.slice(prefix.length);
        const sepIdx = rest.indexOf("__");
        if (sepIdx === -1) continue;
        const env = rest.slice(0, sepIdx);
        const date = rest.slice(sepIdx + 2);
        if (!env || !date) continue;
        let count = 0;
        try {
            count = JSON.parse(localStorage.getItem(key) || "[]").length;
        } catch (e) {
            count = 0;
        }
        result.push({ env, date, count });
    }
    return result.sort((a, b) => b.date.localeCompare(a.date));
}

export function clearFailLogs(env, dateKey) {
    const e = env || getEnvironment();
    const d = dateKey || getTodayKey();
    localStorage.removeItem(buildStorageKey(e, d));
}

export function clearAllEnvLogs(env) {
    const e = env || getEnvironment();
    const prefix = `${LOG_PREFIX}__${e}__`;
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
}

function sanitizeParams(params) {
    if (!params) return null;

    function redact(obj) {
        if (typeof obj !== "object" || obj === null) return obj;
        const sensitiveKeys = ["password", "token", "secret", "authorization"];
        for (const key of Object.keys(obj)) {
            if (sensitiveKeys.includes(key.toLowerCase())) {
                obj[key] = "[REDACTED]";
            } else {
                redact(obj[key]);
            }
        }
        return obj;
    }

    try {
        const clone = JSON.parse(JSON.stringify(params));
        return redact(clone);
    } catch {
        return "[Unable to serialize]";
    }
}

export default {
    addFailLog,
    getFailLogs,
    clearFailLogs,
};
